/**
 * Andon Board - Visual Management Display
 * Large-format real-time monitoring dashboard for TV displays
 * Designed for high visibility from a distance
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import dashboardService from '../services/dashboardService';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Truck,
  AlertCircle,
  Maximize2,
  Minimize2
} from 'lucide-react';

const AndonBoard = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Auto-refresh every 30 seconds
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['andon-dashboard'],
    queryFn: () => dashboardService.getDashboardSummary(),
    refetchInterval: 30000, // 30 seconds
  });

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const metrics = data?.todays_metrics || {};
  const liveTrucks = data?.live_trucks || [];

  // Categorize trucks by status
  const onTimeTrucks = liveTrucks.filter(t => !t.is_breached && !t.is_at_risk);
  const atRiskTrucks = liveTrucks.filter(t => t.is_at_risk && !t.is_breached);
  const delayedTrucks = liveTrucks.filter(t => t.is_breached);

  // Calculate overall status
  const getOverallStatus = () => {
    if (delayedTrucks.length >= 3) return 'critical';
    if (delayedTrucks.length > 0 || atRiskTrucks.length >= 3) return 'warning';
    return 'normal';
  };

  const overallStatus = getOverallStatus();

  const statusColors = {
    critical: {
      bg: 'bg-red-100',
      border: 'border-red-500',
      text: 'text-red-900',
      badge: 'bg-red-500',
      light: 'bg-red-600',
    },
    warning: {
      bg: 'bg-yellow-100',
      border: 'border-yellow-500',
      text: 'text-yellow-900',
      badge: 'bg-yellow-500',
      light: 'bg-yellow-600',
    },
    normal: {
      bg: 'bg-green-100',
      border: 'border-green-500',
      text: 'text-green-900',
      badge: 'bg-green-500',
      light: 'bg-green-600',
    },
  };

  const colors = statusColors[overallStatus];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-4xl">Loading Andon Board...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-6xl font-bold text-white mb-2">ANDON BOARD</h1>
          <p className="text-2xl text-gray-400">Inbound Operations - Real-time Status</p>
        </div>
        <div className="text-right">
          <div className="text-5xl font-bold text-white mb-2">
            {currentTime.toLocaleTimeString()}
          </div>
          <div className="text-2xl text-gray-400">
            {currentTime.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
        <button
          onClick={toggleFullScreen}
          className="text-white hover:text-gray-300 p-4"
        >
          {isFullScreen ? <Minimize2 size={32} /> : <Maximize2 size={32} />}
        </button>
      </div>

      {/* Overall Status Indicator */}
      <div className={`${colors.bg} ${colors.border} border-l-8 rounded-lg p-8 mb-8`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-4xl font-bold ${colors.text} mb-2`}>
              SYSTEM STATUS: {overallStatus.toUpperCase()}
            </h2>
            <p className="text-2xl text-gray-700">
              {delayedTrucks.length} Delayed • {atRiskTrucks.length} At Risk • {onTimeTrucks.length} On Time
            </p>
          </div>
          <div className={`${colors.light} rounded-full p-8 animate-pulse`}>
            {overallStatus === 'critical' ? (
              <AlertTriangle size={80} className="text-white" />
            ) : overallStatus === 'warning' ? (
              <AlertCircle size={80} className="text-white" />
            ) : (
              <CheckCircle size={80} className="text-white" />
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {/* Trucks Received */}
        <div className="bg-gray-800 rounded-lg p-8 border-4 border-blue-500">
          <div className="flex items-center justify-between mb-4">
            <Truck size={48} className="text-blue-500" />
            <div className="text-right">
              <div className="text-7xl font-bold text-white">
                {metrics.trucks_received || 0}
              </div>
              <div className="text-2xl text-gray-400">Received (24h)</div>
            </div>
          </div>
          <div className="text-xl text-gray-400">
            Target: {metrics.daily_target || 0}
          </div>
        </div>

        {/* Completed */}
        <div className="bg-gray-800 rounded-lg p-8 border-4 border-green-500">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle size={48} className="text-green-500" />
            <div className="text-right">
              <div className="text-7xl font-bold text-white">
                {metrics.trucks_completed || 0}
              </div>
              <div className="text-2xl text-gray-400">Completed</div>
            </div>
          </div>
          <div className="text-xl text-gray-400">
            Active: {metrics.active_trucks || 0}
          </div>
        </div>

        {/* SLA Breaches */}
        <div className="bg-gray-800 rounded-lg p-8 border-4 border-red-500">
          <div className="flex items-center justify-between mb-4">
            <AlertTriangle size={48} className="text-red-500" />
            <div className="text-right">
              <div className="text-7xl font-bold text-white">
                {metrics.sla_breaches || 0}
              </div>
              <div className="text-2xl text-gray-400">SLA Breaches</div>
            </div>
          </div>
          <div className="text-xl text-gray-400">
            At Risk: {atRiskTrucks.length}
          </div>
        </div>

        {/* Avg Processing Time */}
        <div className="bg-gray-800 rounded-lg p-8 border-4 border-purple-500">
          <div className="flex items-center justify-between mb-4">
            <Clock size={48} className="text-purple-500" />
            <div className="text-right">
              <div className="text-7xl font-bold text-white">
                {Math.round((metrics.avg_processing_time || 0) / 60)}
              </div>
              <div className="text-2xl text-gray-400">Avg Hours</div>
            </div>
          </div>
          <div className="text-xl text-gray-400">
            {metrics.avg_processing_time || 0}m total
          </div>
        </div>
      </div>

      {/* Critical Alerts Section */}
      {delayedTrucks.length > 0 && (
        <div className="bg-red-900 border-4 border-red-500 rounded-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertTriangle size={48} className="text-red-300 mr-4" />
            <h2 className="text-4xl font-bold text-white">
              CRITICAL ALERTS - ACTION REQUIRED
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {delayedTrucks.slice(0, 6).map((truck) => (
              <div
                key={truck.workflow_id}
                className="bg-red-800 rounded-lg p-6 border-2 border-red-400"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-3xl font-bold text-white mb-2">
                      {truck.truck_number}
                    </div>
                    <div className="text-xl text-red-200 mb-2">
                      {truck.supplier}
                    </div>
                    <div className="text-lg text-red-300">
                      State: {truck.current_state_display}
                    </div>
                    <div className="text-lg text-red-300">
                      Material: {truck.material_type}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-red-300 mb-2">
                      {Math.floor(Math.abs(truck.sla_remaining) / 60)}h {Math.abs(truck.sla_remaining) % 60}m
                    </div>
                    <div className="text-xl text-red-400">OVERDUE</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* At Risk Trucks */}
      {atRiskTrucks.length > 0 && (
        <div className="bg-yellow-900 border-4 border-yellow-500 rounded-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertCircle size={48} className="text-yellow-300 mr-4" />
            <h2 className="text-4xl font-bold text-white">
              ATTENTION NEEDED - AT RISK
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {atRiskTrucks.slice(0, 6).map((truck) => (
              <div
                key={truck.workflow_id}
                className="bg-yellow-800 rounded-lg p-4 border-2 border-yellow-400"
              >
                <div className="text-2xl font-bold text-white mb-1">
                  {truck.truck_number}
                </div>
                <div className="text-lg text-yellow-200 mb-1">
                  {truck.current_state_display}
                </div>
                <div className="text-lg text-yellow-300">
                  {Math.floor(truck.sla_remaining / 60)}h {truck.sla_remaining % 60}m remaining
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* On Time Trucks Summary */}
      <div className="bg-gray-800 rounded-lg p-8 border-4 border-green-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle size={48} className="text-green-500 mr-4" />
            <div>
              <h2 className="text-4xl font-bold text-white">
                ON TIME OPERATIONS
              </h2>
              <p className="text-2xl text-gray-400">
                {onTimeTrucks.length} trucks operating normally
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-6xl font-bold text-green-500">
              {metrics.qc_pass_rate || 0}%
            </div>
            <div className="text-2xl text-gray-400">Quality Rate</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-500 text-xl">
        Auto-refreshing every 30 seconds • Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

export default AndonBoard;
